// Ad-gate modal: shown when a free/anonymous user triggers a gated action.
// Two choices — upgrade (ad-free forever) or continue with ads after a short
// dwell — plus "Maybe later". POLICY: no ad units are ever rendered inside
// this modal (AdSense placement policy forbids ads in overlays); the dwell
// view shows our own promo card while the real ads live in the page
// placements (see ads/placements.ts). Views swap inside ONE modal (openModal
// is single-instance); the returned promise settles exactly once and 'allow'
// is always resolved from a click handler so the caller's window.open/print
// keeps transient user activation.
//
// Adblock policy: a blocked ad SDK or bait-probe hit sends the free path to
// the wall view ('wall' policy) until the blocker is off. SDK timeouts
// without an adblock signal fail open (an outage must never block printing).
import { el, openModal, closeModal } from './../ui/dom'
import { state, getCanvas } from './../state'
import { adsConfig } from './../ads/config'
import type { GateAction, GateVerdict } from './../ads/gate'
import { provider } from './../ads/providers'
import { openUpgrade } from './upgradeModal'

const ACTION_LABEL: Record<GateAction, string> = {
  'print': 'print your label',
  'export': 'download your file',
  'batch-export': 'export your batch PDF',
  'olm-download': 'download your design',
  'mobile-export': 'download your label',
}

export function openAdGate(action: GateAction): Promise<GateVerdict> {
  return new Promise<GateVerdict>((resolve) => {
    let settled = false
    const settle = (verdict: GateVerdict, close = true): void => {
      if (settled) return
      settled = true
      if (close) closeModal() // onClose fires but the settled guard makes it a no-op
      resolve(verdict)
    }

    const body = el('div')
    const view = el('div')
    view.style.cssText = 'padding:12px 8px 4px'
    body.append(view)

    const swap = (...nodes: HTMLElement[]): void => { view.replaceChildren(...nodes) }

    const btn = (label: string, sub: string, testid: string, primary = false): HTMLButtonElement => {
      const b = el('button', 'w-full rounded-lg text-left transition-all') as HTMLButtonElement
      b.type = 'button'
      b.dataset.testid = testid
      b.style.cssText = primary
        ? 'background:#3b82f6;color:white;padding:12px 16px;margin-bottom:10px'
        : 'background:#243042;color:#e2e8f0;padding:12px 16px;margin-bottom:10px;border:1px solid #334155'
      const t = el('div', 'text-sm font-semibold', label)
      const s = el('div', 'text-xs', sub)
      s.style.cssText = primary ? 'color:rgba(255,255,255,0.8);margin-top:2px' : 'color:#94a3b8;margin-top:2px'
      b.append(t, s)
      return b
    }

    const centeredNote = (text: string): HTMLElement => {
      const p = el('p', 'text-sm text-slate-400 text-center', text)
      p.style.cssText = 'margin-bottom:16px;line-height:1.5'
      return p
    }

    // Our own upsell card — the dwell view's content (never an ad unit).
    const promoCard = (): HTMLElement => {
      const card = el('div')
      card.style.cssText = 'background:#1e293b;border:1px solid #334155;border-radius:12px;padding:20px;text-align:center;margin-bottom:14px'
      card.append(
        el('div', 'text-sm font-bold text-white', 'Go ad-free with Pro'),
        el('div', 'text-xs text-slate-400', 'Unlimited printing and exports, batch labels from CSV, premium templates — $3/mo.'),
      )
      return card
    }

    // ---- View C: adblock wall -------------------------------------------
    const viewWall = (retry: () => void): void => {
      const wrap = el('div')
      wrap.dataset.testid = 'adgate-adblock'
      const title = el('h3', 'text-base font-bold text-white text-center', 'Ad blocker detected')
      title.style.marginBottom = '8px'
      wrap.append(title, centeredNote('The free version is ad-supported. Disable your ad blocker for this site and retry, or go ad-free with Pro.'))
      const upgrade = btn('Upgrade to premium', 'Unlimited & ad-free for a small fee', 'adgate-upgrade', true)
      upgrade.addEventListener('click', () => { settle('deny'); openUpgrade('ad_free') })
      const retryBtn = btn('Retry', 'I disabled my ad blocker', 'adgate-retry')
      retryBtn.addEventListener('click', retry)
      wrap.append(upgrade, retryBtn)
      swap(wrap)
    }

    // ---- View B: dwell (promo card + countdown, no ad) -------------------
    const startDwell = (): void => {
      const cfg = adsConfig()
      const check = el('div')
      check.append(centeredNote('One moment…'))
      swap(check)

      void (async () => {
        // Blocked SDK or bait probe = adblock; timeouts are NOT (fail open).
        // The SDK is only ever loaded for ANONYMOUS users (whose page already
        // carries ad placements) — loading it for registered users would let
        // dashboard-side Auto ads inject overlays into their ad-light tier.
        const bait = await provider().detectAdblock()
        const sdk = cfg.clientId && !state.currentUser ? await provider().loadSdk() : 'timeout'
        return bait || sdk === 'blocked'
      })().catch(() => false).then((blocked) => {
        if (settled) return
        if (blocked && cfg.adblockPolicy === 'wall') { viewWall(startDwell); return }

        const wrap = el('div')
        const cont = btn('Continue', '', 'adgate-continue-btn', true) as HTMLButtonElement
        cont.disabled = true
        cont.style.opacity = '0.6'
        const contTitle = cont.querySelector('div') as HTMLElement
        wrap.append(promoCard(), cont)
        swap(wrap)

        let left = Math.ceil(cfg.dwellMs / 1000)
        contTitle.textContent = `Continue in ${left}…`
        const tick = setInterval(() => {
          if (settled) { clearInterval(tick); return }
          left -= 1
          if (left <= 0) {
            clearInterval(tick)
            cont.disabled = false
            cont.style.opacity = '1'
            contTitle.textContent = `Continue — ${ACTION_LABEL[action]}`
          } else {
            contTitle.textContent = `Continue in ${left}…`
          }
        }, 1000)
        cont.addEventListener('click', () => { if (!cont.disabled) settle('allow') })
      })
    }

    // Live thumbnail of the user's own design — the modal interrupts the moment
    // their work is ready, so show the work. (Never an ad: placement policy
    // forbids ad units in overlays, so this stays our content only.)
    const buildPreview = (): HTMLElement | null => {
      try {
        const c = getCanvas()
        const grid = c.getObjects().filter((o) => (o as { isGrid?: boolean }).isGrid)
        const objs = c.getObjects().filter((o) => !(o as { isGrid?: boolean }).isGrid)
        if (objs.length === 0) return null // blank label: nothing to show off
        grid.forEach((o) => { o.visible = false })
        c.renderAll()
        const url = c.toDataURL({ format: 'png', multiplier: Math.min(1, 360 / (c.getWidth() || 1)) })
        grid.forEach((o) => { o.visible = true })
        c.requestRenderAll()
        const card = el('div')
        card.style.cssText = 'text-align:center;margin-bottom:14px'
        const img = document.createElement('img')
        img.src = url
        img.alt = 'Your design'
        // display:inline-block beats the global img{display:block} reset, so
        // the wrapper's text-align:center actually centers the label.
        img.style.cssText = 'display:inline-block;margin:0 auto;max-width:100%;max-height:96px;border-radius:6px;background:#fff;box-shadow:0 4px 16px rgba(0,0,0,0.45)'
        const cap = el('div', 'text-xs font-semibold text-emerald-400', '✓ Your design is ready')
        cap.style.marginTop = '8px'
        card.append(img, cap)
        return card
      } catch { return null } // preview is decoration — never block the gate on it
    }

    // ---- View A: the options ---------------------------------------------
    const viewOptions = (): void => {
      const wrap = el('div')
      const preview = buildPreview()
      if (preview) wrap.append(preview)
      wrap.append(centeredNote(`Pick how you'd like to ${ACTION_LABEL[action]}:`))

      const upgrade = btn('Upgrade to premium — $3/mo', 'Ad-free forever · unlimited exports · batch printing from CSV', 'adgate-upgrade', true)
      upgrade.addEventListener('click', () => { settle('deny'); openUpgrade('ad_free') })
      wrap.append(upgrade)

      const withAds = btn('Continue with ads', `Free forever — a short countdown, then we ${ACTION_LABEL[action]}`, 'adgate-continue')
      withAds.addEventListener('click', startDwell)
      wrap.append(withAds)

      const dismiss = el('button', 'w-full py-2 text-sm text-slate-500 hover:text-slate-300 transition-colors mt-1', 'Maybe later')
      dismiss.type = 'button'
      dismiss.dataset.testid = 'adgate-dismiss'
      dismiss.addEventListener('click', () => settle('deny'))
      wrap.append(dismiss)
      swap(wrap)
    }

    viewOptions()
    openModal({
      title: 'Keep creating for free',
      body,
      width: '440px',
      testid: 'ad-gate-modal',
      onClose: () => settle('deny', false), // ✕ / backdrop / replaced by another modal
    })
  })
}
